import React, { useState, useRef, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import Table from 'Components/Table';
import { Text, Box, Icon, ICON_LIST } from 'Components/Base';
import Button from 'Components/ButtonIcon';
import { MODULE_STATUS, RANK_STATUS, RAW_CONTENT_TYPE, formatWatchedPercent } from 'Domains/module';
import { SYLLABUS_MODE } from 'Domains/syllabus';

const TextWrapper = styled(Box)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: center;
`;

const AttendanceTableStyle = styled(Table)`
  & .MuiTableCell-sizeSmall {
    padding: 5px 10px;
    border-right: 1px solid ${({ theme }) => theme.colors.white};
    border-bottom: none;

    &:nth-last-child(1) {
      border-right: none;
    }
  }

  & .MuiTableRow-head:nth-child(1) {
    & .MuiTableCell-head {
      &:nth-child(n + 5) {
        border-bottom: 1px solid ${({ theme }) => theme.colors.white};
      }
    }
  }

  & .MuiTableRow-head:nth-child(2) {
    & .MuiTableCell-head {
      &:nth-child(1) {
        min-width: 100px;
        width: 100px;
      }
    }
  }

  // FIXME: [LE-54] Background color on last table cell.
  & .MuiTableRow-root {
    .MuiTableCell-body:nth-last-child(1) {
      padding: 0;
    }
    .MuiTableCell-body {
      height: 44px;
    }
  }
`;

const TKAttendanceTableStyle = styled(Table)`
  & .MuiTableCell-head {
    white-space: normal;
    word-break: break-word;
  }

  & .MuiTableCell-sizeSmall {
    padding: 5px 10px;
    border-right: 1px solid ${({ theme }) => theme.colors.white};
    border-bottom: none;

    &:nth-last-child(1) {
      border-right: none;
    }
  }

  & .MuiTableRow-head:nth-child(1) {
    & .MuiTableCell-head {
      &:nth-child(n + 5) {
        border-bottom: 1px solid ${({ theme }) => theme.colors.white};
      }
    }
  }

  & .MuiTableRow-head:nth-child(2) {
    & .MuiTableCell-head {
      &:nth-child(n + 5) {
        border-bottom: 1px solid ${({ theme }) => theme.colors.white};
      }
    }
  }

  & .MuiTableRow-head:nth-child(3) {
    & .MuiTableCell-head {
      &:nth-child(1) {
        min-width: 100px;
        width: 100px;
      }

      &:nth-child(-n + 4) {
        > div {
          position: relative;
          top: -35px;
        }
        &:nth-child(4) {
          > div {
            display: flex;
            justify-content: center;
          }
        }
      }
    }
  }

  // FIXME: [LE-54] Background color on last table cell.
  & .MuiTableRow-root {
    .MuiTableCell-body:nth-last-child(1) {
      padding: 0;
    }
    .MuiTableCell-body {
      height: 44px;
    }
  }
`;

const getBackgroundColor = rank => {
  if (rank === RANK_STATUS.GOOD) {
    return 'success';
  } else if (rank === RANK_STATUS.NORMAL) {
    return 'warning';
  } else if (rank === RANK_STATUS.NEED_IMPROVEMENT) {
    return 'dangerAlt';
  }
};

const TableHead = props => <Text fontWeight={600} {...props} />;

// Shared expand state so clicking any topic header expands/collapses them all together.
const HeaderExpandContext = React.createContext({ expanded: false, toggle: () => {} });

// Expand/collapse chevron, slightly larger with a stroke to read bolder.
const ChevronWrap = styled.span`
  display: flex;
  justify-content: center;
  line-height: 0;
  margin-top: 1px;
  font-size: 18px;
  & svg {
    stroke: currentColor;
    stroke-width: 1.3px;
  }
`;

// Subsection/topic header: clamp to 2 lines; a chevron shows (only when the name is
// long enough to be cut off) to signal it can be expanded. Click toggles ALL headers.
const ExpandableHeader = ({ children }) => {
  const { expanded, toggle } = useContext(HeaderExpandContext);
  const [overflowing, setOverflowing] = useState(false);
  const textRef = useRef(null);

  // Measure overflow only while collapsed and remember it, so a short name that fits
  // never shows the chevron — even when everything is expanded together.
  useEffect(() => {
    const el = textRef.current;
    if (el && !expanded) setOverflowing(el.scrollHeight - el.clientHeight > 1);
  }, [children, expanded]);

  const canToggle = overflowing;

  return (
    <TableHead
      onClick={canToggle ? toggle : undefined}
      style={{ cursor: canToggle ? 'pointer' : 'default', margin: '0 -6px' }}
    >
      <span
        ref={textRef}
        style={{
          display: expanded ? 'block' : '-webkit-box',
          WebkitLineClamp: expanded ? 'unset' : 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: 1.3,
          textAlign: 'center',
        }}
      >
        {children}
      </span>
      {canToggle && (
        <ChevronWrap>
          <Icon name={expanded ? ICON_LIST.MENU_EXPAND_LESS : ICON_LIST.MENU_EXPAND_MORE} />
        </ChevronWrap>
      )}
    </TableHead>
  );
};

const HeaderColSpanText = styled(TableHead)`
  /* border-bottom: 1px solid ${({ theme }) => theme.colors.white}; */
  white-space: normal;
  word-break: break-word;
  text-align: center;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// Video progress: ✓ watched past 50% / ◐ started but ≤50% / ✗ not started.
const COMPLETION_ICON = {
  [MODULE_STATUS.COMPLETED]: { name: ICON_LIST.CHECK, color: 'primary' },
  [MODULE_STATUS.IN_PROGRESS]: { name: ICON_LIST.HALF_CIRCLE, color: 'primary' },
  [MODULE_STATUS.INCOMPLETED]: { name: ICON_LIST.CLOSE, color: 'danger' },
};

const CompletionCell = ({ cell: { value } }) => {
  const { status, watchedPercent } = value;
  const { name, color } = COMPLETION_ICON[status] ?? COMPLETION_ICON[MODULE_STATUS.INCOMPLETED];
  const title =
    status === MODULE_STATUS.IN_PROGRESS
      ? `ดูไปแล้ว ${formatWatchedPercent(watchedPercent)}`
      : undefined;
  return (
    <Box display={'flex'} justifyContent={'center'} title={title}>
      <Text color={color} display={'flex'} alignItems={'center'}>
        <Icon name={name} style={{ marginRight: '10px' }} />
      </Text>
    </Box>
  );
};

const QuizScoreCell = ({ cell: { value } }) => {
  const { status } = value;
  if (status === MODULE_STATUS.INCOMPLETED) {
    return (
      <Text display={'flex'} color="danger">
        <Icon name={ICON_LIST.CLOSE} style={{ marginLeft: 'auto', marginRight: 'auto' }} />
      </Text>
    );
  }
  const { score, rank } = value?.quizResult ?? {};
  return (
    <Box bg={getBackgroundColor(rank)} px={'10px'} py={'5px'}>
      <TextWrapper>{score}</TextWrapper>
    </Box>
  );
};

// Some quiz names already start with "Q:", so drop it to avoid rendering "Q: Q: name".
const stripQuizPrefix = name => (name ?? '').replace(/^\s*Q\s*:\s*/i, '');

// Branch on contentType, not __typename: QuizFeed rows may arrive as FOSectionReportQuizResult
// with a meaningless quizResult, but they are completion-only like Video.
const getSubsectionColumn = (subsection, subsectionIdx, t) => {
  const { contentType, subsectionName } = subsection;
  const column = {
    id: `subsection${subsectionIdx}`,
    width: 110,
    accessor: `subsections[${subsectionIdx}]`,
    disableSortBy: true,
  };

  if (contentType === RAW_CONTENT_TYPE.QUIZ) {
    return {
      ...column,
      Header: <ExpandableHeader>Q: {stripQuizPrefix(subsectionName)}</ExpandableHeader>,
      Cell: QuizScoreCell,
    };
  } else if (contentType === RAW_CONTENT_TYPE.QUIZ_FEED) {
    return {
      ...column,
      Header: <ExpandableHeader>Q: Adaptive Quiz - {subsectionName}</ExpandableHeader>,
      Cell: CompletionCell,
    };
  }
  // Video, and any content type we don't know yet, render as completion so the table never breaks.
  return {
    ...column,
    Header: (
      <ExpandableHeader>
        {t('SUBSECTION')}: {subsectionName}
      </ExpandableHeader>
    ),
    Cell: CompletionCell,
  };
};

const PAGE_SIZE = 50;

// maxHeight is not styled system
export const AttendanceTable = ({ data, maxHeight, translation, mode, ...props }) => {
  const { t } = useTranslation('studentSummary');
  const { sectionInfos = {}, reports = [] } = data ?? {};
  const [currentPage, setCurrentPage] = useState(1);
  const [allExpanded, setAllExpanded] = useState(false);
  // Memoize the context value so header cells don't re-render on every table render
  // (e.g. load-more, data refetch) unless `allExpanded` actually changes.
  const toggleExpanded = React.useCallback(() => setAllExpanded(prev => !prev), []);
  const headerExpandValue = React.useMemo(
    () => ({ expanded: allExpanded, toggle: toggleExpanded }),
    [allExpanded, toggleExpanded]
  );

  const columns = React.useMemo(() => {
    let subsectionIdx = -1;
    return [
      {
        id: 'classNo',
        Header: <TableHead>{t('CLASS')}</TableHead>,
        accessor: properties => properties.schoolGrade.shortName + '/' + properties.classNo,
        width: 100,
        Cell: ({ cell: { row } }) => (
          <TextWrapper minWidth={'100px'}>
            {row?.original?.schoolGrade.shortName}/{row?.original?.classNo}
          </TextWrapper>
        ),
      },
      {
        id: 'number',
        Header: <TableHead>{t('NUMBER')}</TableHead>,
        accessor: 'studentNo',
        width: 100,
        Cell: ({ cell: { value } }) => (
          <Box>
            <TextWrapper>{value}</TextWrapper>
          </Box>
        ),
      },
      {
        id: 'username',
        Header: <TableHead>{t('USERNAME')}</TableHead>,
        accessor: 'classroomUsername',
        width: 140,
        Cell: ({ cell: { value } }) => (
          <Box>
            <TextWrapper minWidth={'140px'}>{value}</TextWrapper>
          </Box>
        ),
      },
      {
        id: 'fullname',
        width: 160,
        Header: <HeaderColSpanText>{t('FULLNAME')}</HeaderColSpanText>,
        accessor: properties => properties.firstName + ' ' + properties.lastName,
        Cell: ({ cell: { value } }) => (
          <Box>
            <TextWrapper style={{ textAlign: 'left' }}>{value}</TextWrapper>
          </Box>
        ),
      },
      ...(_.isEmpty(reports)
        ? [
            {
              id: 'topic',
              Header: (
                <HeaderColSpanText width={'100px'} mx={'auto'}>
                  {t('TOPIC')}
                </HeaderColSpanText>
              ),
              columns: [
                {
                  id: 'subsection',
                  Header: <TableHead>{t('SUBSECTION')}</TableHead>,
                },
              ],
            },
          ]
        : sectionInfos.map(sectionInfo => {
            return {
              id: sectionInfo.sectionCode,
              Header: (
                <HeaderColSpanText>
                  {reports.length > 0 ? `${t('TOPIC')}: ${sectionInfo.sectionName}` : t('TOPIC')}
                </HeaderColSpanText>
              ),
              columns:
                reports.length > 0
                  ? sectionInfo?.subsectionInfos?.map(subsection => {
                      subsectionIdx += 1;
                      return getSubsectionColumn(subsection, subsectionIdx, t);
                    })
                  : [
                      {
                        id: `subsection`,
                        disableSortBy: true,
                        Header: <TableHead>{t('SUBSECTION')}</TableHead>,
                      },
                    ],
            };
          })),
    ];
  }, [reports]);

  const tkColumns = React.useMemo(() => {
    let sectionGroupCodex = -1;
    let subsectionIdx = -1;
    return [
      {
        id: 'classNo',
        Header: <TableHead>{t('CLASS')}</TableHead>,
        accessor: properties => properties.schoolGrade.shortName + '/' + properties.classNo,
        width: 100,
        Cell: ({ cell: { row } }) => (
          <TextWrapper minWidth={'100px'}>
            {row?.original?.schoolGrade.shortName}/{row?.original?.classNo}
          </TextWrapper>
        ),
      },
      {
        id: 'number',
        Header: <TableHead>{t('NUMBER')}</TableHead>,
        accessor: 'studentNo',
        width: 100,
        Cell: ({ cell: { value } }) => (
          <Box>
            <TextWrapper>{value}</TextWrapper>
          </Box>
        ),
      },
      {
        id: 'username',
        Header: <TableHead>{t('USERNAME')}</TableHead>,
        accessor: 'classroomUsername',
        width: 140,
        Cell: ({ cell: { value } }) => (
          <Box>
            <TextWrapper minWidth={'140px'}>{value}</TextWrapper>
          </Box>
        ),
      },
      {
        id: 'fullname',
        width: 160,
        Header: <HeaderColSpanText>{t('FULLNAME')}</HeaderColSpanText>,
        accessor: properties => properties.firstName + ' ' + properties.lastName,
        Cell: ({ cell: { value } }) => (
          <Box>
            <TextWrapper style={{ textAlign: 'left' }}>{value}</TextWrapper>
          </Box>
        ),
      },
      ...(_.isEmpty(reports)
        ? [
            {
              id: 'topic',
              Header: (
                <HeaderColSpanText width={'100px'} mx={'auto'}>
                  {t('TOPIC')}
                </HeaderColSpanText>
              ),
              columns: [
                {
                  id: 'mission',
                  Header: <HeaderColSpanText>{t('MISSION')}</HeaderColSpanText>,
                  columns: [
                    {
                      id: 'subsection',
                      Header: <TableHead>{t('SUBSECTION')}</TableHead>,
                    },
                  ],
                },
              ],
            },
          ]
        : sectionInfos.map(sectionInfo => {
            return {
              id: sectionInfo.sectionCode,
              Header: (
                <HeaderColSpanText>
                  {reports.length > 0 ? `${t('TOPIC')}: ${sectionInfo.sectionName}` : t('TOPIC')}
                </HeaderColSpanText>
              ),
              columns:
                reports.length > 0
                  ? sectionInfo?.sectionGroupInfos?.map(mission => {
                      const { sectionGroupName, subsectionInfos } = mission;
                      sectionGroupCodex += 1;
                      return {
                        id: `mission${sectionGroupCodex}`,
                        Header: (
                          <HeaderColSpanText>
                            {t('MISSION')}: {sectionGroupName}
                          </HeaderColSpanText>
                        ),
                        columns: subsectionInfos.map(subsection => {
                          subsectionIdx += 1;
                          return getSubsectionColumn(subsection, subsectionIdx, t);
                        }),
                      };
                    })
                  : [
                      {
                        id: `mission`,
                        disableSortBy: true,
                        Header: <TableHead>{t('MISSION')}</TableHead>,
                      },
                    ],
            };
          })),
    ];
  }, [reports]);

  const AttendanceTableBlock = React.useMemo(
    () => (
      <AttendanceTableStyle
        dense
        stickyHeader
        columns={columns}
        stickyColumnIds={['classNo', 'number', 'username', 'fullname']}
        data={data?.reports ? data.reports.slice(0, currentPage * PAGE_SIZE) : []}
        style={{ maxHeight }}
        {...props}
      />
    ),
    [data, currentPage]
  );

  const TKAttendanceTableBlock = React.useMemo(
    () => (
      <TKAttendanceTableStyle
        dense
        stickyHeader
        columns={tkColumns}
        stickyColumnIds={['classNo', 'number', 'username', 'fullname']}
        data={data?.reports ? data.reports.slice(0, currentPage * PAGE_SIZE) : []}
        style={{ maxHeight }}
        {...props}
      />
    ),
    [data, currentPage]
  );

  const onLoadMore = React.useCallback(() => {
    setCurrentPage(currentPage + 1);
  });

  const isShowLoadMoreButton =
    data && data.reports && data.reports.length > currentPage * PAGE_SIZE;

  return (
    <HeaderExpandContext.Provider value={headerExpandValue}>
      <Box>
        {mode === SYLLABUS_MODE.TRADITIONAL ? AttendanceTableBlock : TKAttendanceTableBlock}
        {isShowLoadMoreButton && (
          <Button
            flex="0 0 auto"
            variant={'primary'}
            textProp={{ fontWeight: 'semibold' }}
            lineHeight={'0.5'}
            height={'40px'}
            marginTop={2}
            onClick={onLoadMore}
            message={t('LOAD_MORE')}
          />
        )}
      </Box>
    </HeaderExpandContext.Provider>
  );
};

AttendanceTable.propTypes = {
  data: PropTypes.array.isRequired,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  mode: PropTypes.string,
};

export default AttendanceTable;
